import type { ReactNode } from "react";
import { Dialog as RadixDialog } from "radix-ui";
import { X } from "lucide-react";

import { cn } from "@/lib/cn";
import { IconButton } from "@/components/ui/IconButton";

export interface SidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** One quiet line under the title: a position, a date, a reference. */
  subtitle?: ReactNode;
  /** Before the title: an avatar, a type mark. */
  media?: ReactNode;
  /** Beside the close button: a status chip, a link to the full page. */
  actions?: ReactNode;
  /** Pinned to the bottom: the decision. The primary action goes last. */
  footer?: ReactNode;
  size?: "md" | "lg";
  children: ReactNode;
}

const SIZES = { md: "sm:max-w-md", lg: "sm:max-w-xl" };

/**
 * A record, opened beside the list it came from.
 *
 * The list stays where it was, the panel
 * slides in from the right, and closing it puts you back on the row you
 * left. A dialog asks a question; this shows a thing, so it is as tall as
 * the window, scrolls on its own, and keeps its decision at the bottom
 * where a thumb or a pointer finds it without reading for it. On a phone it
 * is the whole screen.
 */
export function SidePanel({
  open,
  onOpenChange,
  title,
  subtitle,
  media,
  actions,
  footer,
  size = "md",
  children,
}: SidePanelProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        {/* Lighter than a dialog's: the list behind is still the context. */}
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-scrim/25" />
        <RadixDialog.Content
          aria-describedby={undefined}
          className={cn(
            "fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-surface-raised shadow-pop",
            "border-l border-line outline-none",
            SIZES[size],
          )}
        >
          <div className="flex items-start gap-3 border-b border-line px-5 py-4">
            {media ? <div className="shrink-0">{media}</div> : null}
            <div className="min-w-0 flex-1">
              <RadixDialog.Title className="truncate text-lg font-semibold text-fg">
                {title}
              </RadixDialog.Title>
              {subtitle ? (
                <div className="mt-0.5 truncate text-xs text-fg-subtle">{subtitle}</div>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              {actions}
              <RadixDialog.Close asChild>
                <IconButton tipless label="Close" size="sm" icon={<X className="size-4" />} />
              </RadixDialog.Close>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">{children}</div>

          {footer ? (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">
              {footer}
            </div>
          ) : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/** A label and its value on one line, for the body of a side panel. */
export function PanelRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[8.5rem_1fr] items-baseline gap-3 py-2">
      <dt className="text-xs text-fg-subtle">{label}</dt>
      <dd className="min-w-0 text-base text-fg">{children}</dd>
    </div>
  );
}
